/**
 * Validate: pick the protocol and table, declare the period, drop the file,
 * read the results, then submit to compliance or file into an open audit.
 */
import React, { useEffect, useMemo, useState } from 'react';
import ResultsView from './ResultsView';
import UniverseRequestAcknowledge from '../../components/UniverseRequestAcknowledge.jsx';
import { Card, Pill, Notice, Modal, StepChips, OptionCard, btn, input, label as labelCls } from './ui';
import { PROTOCOLS, TABLES, TYPE_TO_CODE, getJson, postForm, postJson, downloadBlob, tableLabel, toCms, userEmail, fmtNum } from './api';

const STEPS = ['Protocol', 'Table', 'Period', 'File', 'Results'];

function Dropzone({ onFile, busy, accept = '.xlsx,.xls,.csv,.txt' }) {
  const [over, setOver] = useState(false);
  return (
    <label onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) onFile(f); }}
      className={`block rounded-2xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition ${over ? 'border-orange-400 bg-orange-50/60' : 'border-slate-200 bg-slate-50/70 hover:bg-slate-50 hover:border-orange-200'}`}>
      <input type="file" className="hidden" accept={accept} disabled={busy} onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) onFile(f); e.target.value = ''; }} />
      <div className="text-sm font-semibold text-slate-800">{busy ? 'Validating…' : 'Drop the universe file here, or click to choose it'}</div>
      <div className="text-xs text-slate-500 mt-1">Excel workbook or CSV, exactly as exported from the plan's system. The file is validated in memory and is not stored by this step.</div>
    </label>
  );
}

const isIn = (i) => ['received', 'accepted'].includes(i?.status);

// A pending universe request (deep link ?universe_request_id=) names the table
// and the period; the Validate screen starts there, on the first table still
// owed (one already received is not asked for again).
function fromPending(pending) {
  const items = pending?.items || [];
  const item = items.find((i) => !isIn(i)) || items[0];
  if (!item) return null;
  const type = String(item.catalog_id || '').toLowerCase();
  for (const key of Object.keys(TABLES)) {
    const t = TABLES[key].find((x) => x.universeType === type || (type === 'snpe' && x.universeType === 'snpcc_snpe'));
    if (t) return { protocol: key, table: t, period: { startDate: pending.request.reporting_period_start || '', endDate: pending.request.reporting_period_end || '' } };
  }
  return null;
}

export default function ValidateView({ user, resubmit, onResubmitDone, pending, onDismissPending }) {
  const pre = useMemo(() => fromPending(pending), [pending]);
  const [protocol, setProtocol] = useState(resubmit?.protocol || pre?.protocol || null);
  const [table, setTable] = useState(pre?.table || null);
  const [period, setPeriod] = useState({ startDate: resubmit?.period?.startDate || pre?.period?.startDate || '', endDate: resubmit?.period?.endDate || pre?.period?.endDate || '' });
  useEffect(() => {
    if (!pre || resubmit) return;
    setProtocol(pre.protocol); setTable(pre.table);
    setPeriod({ startDate: pre.period.startDate || '', endDate: pre.period.endDate || '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pre]);
  const [engagementDate, setEngagementDate] = useState('');
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [headerCorrections, setHeaderCorrections] = useState({});
  const [showSubmit, setShowSubmit] = useState(false);
  const [hrat, setHrat] = useState(null);
  const [hratBusy, setHratBusy] = useState(false);

  const proto = PROTOCOLS.find((p) => p.key === protocol) || null;
  const needsPeriod = !!proto?.needsPeriod;
  const step = !protocol ? 0 : !table ? 1 : needsPeriod && !(period.startDate && period.endDate) ? 2 : !result ? 3 : 4;

  const declaredPeriod = useMemo(() => {
    if (needsPeriod) return period.startDate && period.endDate ? period : null;
    // SNPCC and CPE universes are "as of" the engagement date: declare the CMS
    // 12 month window ending there, so the COA picker can match months.
    const d = String(engagementDate || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
    const [y, m, day] = d.split('-').map(Number);
    const s = new Date(Date.UTC(y - 1, m - 1, day));
    return Number.isNaN(s.getTime()) ? null : { startDate: s.toISOString().slice(0, 10), endDate: d };
  }, [needsPeriod, period, engagementDate]);

  const reset = () => { setTable(null); setFile(null); setResult(null); setError(''); setHeaderCorrections({}); setHrat(null); };
  const startOver = () => { setProtocol(null); reset(); setPeriod({ startDate: '', endDate: '' }); setEngagementDate(''); };
  // A finished step chip jumps back to that step.
  const jump = (i) => {
    if (i === 0) startOver();
    else if (i === 1) reset();
    else if (i === 2) { setFile(null); setResult(null); setHrat(null); setPeriod({ startDate: '', endDate: '' }); }
    else if (i === 3) { setFile(null); setResult(null); setHrat(null); }
  };

  async function run(f, corrections = headerCorrections) {
    if (!table) return;
    if (table.engagement && !engagementDate) { setError('Enter the engagement letter date first.'); return; }
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', f, f.name);
      let path = '/upload';
      if (table.endpoint) {
        path = table.endpoint;
        if (table.engagement) fd.append(protocol === 'CPE' ? 'engagementDate' : 'engagementLetterDate', engagementDate);
      } else {
        fd.append('universeType', table.universeType);
        fd.append('headerCorrections', JSON.stringify(corrections || {}));
        if (period.startDate && period.endDate) { fd.append('universePeriodStart', toCms(period.startDate)); fd.append('universePeriodEnd', toCms(period.endDate)); }
      }
      const data = await postForm(path, fd);
      setFile(f); setResult(data); setHrat(null);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  }

  async function exportResults(format) {
    if (!result || !table) return;
    const name = `${table.universeType}_${format === 'csv' ? 'export.csv' : 'export.xlsx'}`;
    try {
      if (format === 'xlsx' && result.universeMode === 'issues-only' && file) {
        const fd = new FormData();
        fd.append('file', file, file.name); fd.append('universeType', table.universeType); fd.append('tableLabel', tableLabel(table));
        if (declaredPeriod) fd.append('universePeriod', JSON.stringify(declaredPeriod));
        if (protocol === 'SNPCC' && engagementDate) fd.append('engagementLetterDate', engagementDate);
        await downloadBlob('/export/full', { method: 'POST', body: fd, filename: `${table.universeType}_complete_export.xlsx` });
        return;
      }
      await downloadBlob('/export', { method: 'POST', filename: name, json: {
        format, errors: result.errors || [], universe: result.universe || [], universeMode: result.universeMode || 'full',
        cleanRowsOmitted: result.universeCleanRowsOmitted || 0, timeliness: result.timeliness || null,
        universeRowCount: result.totalRows || result.stats?.totalRows || 0, errorCountTotal: result.totalErrorCount || 0,
        filename: table.universeType, tableLabel: tableLabel(table), timelinessSummary: result.timelinessSummary || null, monthlyTimeliness: result.monthlyTimeliness || null,
      } });
    } catch (e) { setError(e.message); }
  }

  async function generateHrat() {
    setHratBusy(true); setError('');
    try { setHrat(await postJson('/snpcc/generate-hrat-ia', { universe: result.universe, engagementLetterDate: engagementDate })); }
    catch (e) { setError(e.message); } finally { setHratBusy(false); }
  }

  const periodChip = declaredPeriod ? `${declaredPeriod.startDate} to ${declaredPeriod.endDate}` : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <StepChips steps={STEPS} current={step} onJump={jump} />
        <div className="flex items-center gap-3">
          {resubmit && <Pill tone="amber">Resubmitting submission #{resubmit.id} · {resubmit.protocol} {resubmit.tableLabel}</Pill>}
          {step > 0 && <button type="button" className={btn.link} onClick={startOver}>Start over</button>}
        </div>
      </div>

      {error && <Notice tone="red">{error}</Notice>}

      {pending && (
        <Notice tone="sky">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="text-sm font-semibold">Pending universe request{pending.request.sequence_number ? ` · cycle ${pending.request.sequence_number}` : ''}: {pending.request.title}</div>
              <div className="text-xs mt-0.5">{pending.items.length} item{pending.items.length === 1 ? '' : 's'} expected: {pending.items.map((i) => (isIn(i) ? `${i.catalog_label} (received)` : i.catalog_label)).join(' · ')}{pending.request.reporting_period_start ? ` · period ${pending.request.reporting_period_start} to ${pending.request.reporting_period_end}` : ''}</div>
              {pending.items.some((i) => !isIn(i)) ? (
                <>
                  <div className="text-xs mt-1">{pre ? 'The table and period are set from the request. Drop the file to validate it, then submit it as final; compliance sees your submission against this request.' : 'Validate the requested file and submit it as final; compliance sees your submission against this request.'}</div>
                  <UniverseRequestAcknowledge request={pending.request} autoOpen={!!pending.wantsAck} compact mode={pending.mode || 'recipient'} consumerApp="scrubber" />
                </>
              ) : (
                <div className="text-xs mt-1">Everything this request asks of the Scrubber is in. The request waits on its other items.</div>
              )}
            </div>
            <button type="button" onClick={onDismissPending} className="text-sky-400 hover:text-sky-700 text-lg leading-none" title="Dismiss">×</button>
          </div>
        </Notice>
      )}

      {step === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PROTOCOLS.map((p) => (
            <button key={p.key} type="button" onClick={() => setProtocol(p.key)}
              className="text-left bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md hover:border-orange-200 transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="text-[13.5px] font-semibold text-slate-900">{p.label}</div>
                <Pill tone="slate">{p.part}</Pill>
              </div>
              <div className="mt-1 text-[11.5px] text-slate-500">{p.title}</div>
              <div className="mt-2 text-[11px] text-slate-500">{TABLES[p.key].length} universe table{TABLES[p.key].length === 1 ? '' : 's'}</div>
            </button>
          ))}
        </div>
      )}

      {step === 1 && (
        <Card title={`${proto.label}: ${proto.title}`} subtitle="Pick the universe table the file follows. The column layout is checked against the CMS record layout for that table." right={<button type="button" className={btn.secondarySmall} onClick={() => setProtocol(null)}>Change protocol</button>}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TABLES[protocol].map((t) => (
              <button key={t.universeType} type="button" onClick={() => setTable(t)}
                className="text-left bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md hover:border-orange-200 transition-all">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-[13.5px] font-semibold text-slate-900">{t.label}</div>
                  <Pill tone="orange">{t.code}</Pill>
                </div>
                <div className="mt-1 text-[11.5px] text-slate-500">{t.name}</div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card title={`${proto.label} ${tableLabel(table)}`} subtitle="Declare the universe period. Cases whose qualifying date falls outside it are flagged, and the COA sync matches the months you declare." right={<button type="button" className={btn.secondarySmall} onClick={reset}>Change table</button>}>
          <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
            <div><label className={labelCls}>Period start</label><input type="date" className={input} value={period.startDate} onChange={(e) => setPeriod({ ...period, startDate: e.target.value })} /></div>
            <div><label className={labelCls}>Period end</label><input type="date" className={input} value={period.endDate} onChange={(e) => setPeriod({ ...period, endDate: e.target.value })} /></div>
          </div>
          {period.startDate && period.endDate && new Date(period.startDate) > new Date(period.endDate) && <div className="text-xs text-rose-600 mt-2">The start date is after the end date.</div>}
        </Card>
      )}

      {step === 3 && (
        <Card title={`${proto.label} ${tableLabel(table)}`} subtitle={periodChip ? `Period ${periodChip}` : table.engagement ? 'This table is validated as of the audit engagement letter date.' : undefined}
          right={<button type="button" className={btn.secondarySmall} onClick={() => { if (needsPeriod) setPeriod({ startDate: '', endDate: '' }); else reset(); }}>{needsPeriod ? 'Change period' : 'Change table'}</button>}>
          {table.engagement && (
            <div className="max-w-xs mb-4"><label className={labelCls}>Engagement letter date</label><input type="date" className={input} value={engagementDate} onChange={(e) => setEngagementDate(e.target.value)} /></div>
          )}
          <Dropzone onFile={(f) => run(f)} busy={busy} />
        </Card>
      )}

      {step === 4 && (
        <>
          <ResultsView
            result={result}
            title={`${proto.label} ${tableLabel(table)}`}
            meta={<>{file?.name}{periodChip ? ` · ${periodChip}` : ''}{engagementDate ? ` · engagement letter ${engagementDate}` : ''}</>}
            headerCorrections={headerCorrections}
            onHeaderCorrections={(c) => { setHeaderCorrections(c); }}
            actions={(
              <>
                {Object.values(headerCorrections).some(Boolean) && <button type="button" className={btn.small} disabled={busy} onClick={() => run(file, headerCorrections)}>Run again with the corrected headers</button>}
                {protocol === 'SNPCC' && result?.hratIAEligibleCount > 0 && !hrat && <button type="button" className={btn.secondarySmall} disabled={hratBusy} onClick={generateHrat}>{hratBusy ? 'Generating…' : `Generate Table 2IA (${result.hratIAEligibleCount})`}</button>}
                <button type="button" className={btn.secondarySmall} onClick={() => exportResults('xlsx')}>Export Excel</button>
                <button type="button" className={btn.secondarySmall} onClick={() => exportResults('csv')}>Export CSV</button>
                <button type="button" className={btn.secondarySmall} onClick={() => { setFile(null); setResult(null); setHrat(null); }}>Another file</button>
                <button type="button" className={btn.primary} onClick={() => setShowSubmit(true)}>Submit as final</button>
              </>
            )}
          />
          {hrat && <HratTable hrat={hrat} onExport={() => downloadBlob('/snpcc/export-hrat-ia', { method: 'POST', json: { rows: hrat.rows }, filename: 'HRAT-IA_Table_2IA.xlsx' }).catch((e) => setError(e.message))} />}
          <SubmitDialog open={showSubmit} onClose={() => setShowSubmit(false)} user={user} file={file} result={result} table={table} protocol={protocol} period={declaredPeriod} resubmit={resubmit}
            onDone={(msg) => { setShowSubmit(false); setError(''); if (resubmit && onResubmitDone) onResubmitDone(msg); }} />
        </>
      )}
    </div>
  );
}

function HratTable({ hrat, onExport }) {
  const rows = hrat?.rows || [];
  if (!rows.length) return <Notice tone="slate">No enrollees with timeliness issues for Table 2IA.</Notice>;
  const cols = Object.keys(rows[0]);
  return (
    <Card title="Table 2IA (HRAT impact analysis)" subtitle={`${fmtNum(hrat.count || rows.length)} enrollees. Generated from the SNPE universe you validated.`} right={<button type="button" className={btn.secondarySmall} onClick={onExport}>Export Table 2IA</button>} padding="p-0">
      <div className="overflow-auto max-h-[50vh]">
        <table className="text-xs min-w-full">
          <thead className="sticky top-0 bg-slate-50 border-b border-slate-200"><tr>{cols.map((c) => <th key={c} className="px-3 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">{c}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">{rows.slice(0, 500).map((r, i) => <tr key={i}>{cols.map((c) => <td key={c} className="px-3 py-1.5 whitespace-nowrap text-slate-700">{String(r[c] ?? '')}</td>)}</tr>)}</tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * Submit as final: whose universe it is, and where it goes. Monitoring sends
 * the original file with the results to compliance (held, encrypted, until the
 * review); an open audit files it onto the matching universe slot in Core.
 */
function SubmitDialog({ open, onClose, user, file, result, table, protocol, period, resubmit, onDone }) {
  const [entities, setEntities] = useState([]);
  const [entity, setEntity] = useState('Internal');
  const [dest, setDest] = useState('monitoring');
  const [audits, setAudits] = useState(null);
  const [orgName, setOrgName] = useState(null);
  const [slotId, setSlotId] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [done, setDone] = useState(null);
  const myCode = TYPE_TO_CODE[String(table?.universeType || '').toLowerCase()] || null;

  useEffect(() => {
    if (!open) return;
    setDest('monitoring'); setSlotId(''); setNote(''); setDone(null); setBusy(false);
    getJson('/fdr-entities').then((d) => setEntities(d.entities || [])).catch(() => setEntities([]));
    getJson('/audit-slots').then((d) => { setAudits(d.audits || []); setOrgName(d.organization || null); }).catch(() => setAudits([]));
  }, [open]);

  const slots = useMemo(() => {
    const out = [];
    for (const a of audits || []) for (const s of a.slots || []) out.push({ id: s.id, code: s.code, status: s.status, label: `${String(a.program || '').toUpperCase()} ${a.cycle_label || ''} · ${s.label}` });
    return myCode ? out.filter((o) => o.code === myCode) : out;
  }, [audits, myCode]);
  useEffect(() => { if (open && !slotId && slots.length && myCode) setSlotId(slots[0].id); }, [open, slots, myCode, slotId]);

  if (!open) return null;
  const totalIssues = result?.totalErrorCount ?? (result?.errors || []).length;

  async function confirm() {
    setBusy(true); setNote('');
    try {
      if (dest === 'monitoring') {
        if (!entity) { setNote('State whose universe this is.'); setBusy(false); return; }
        const payload = {
          selectedTable: { universeType: table.universeType, label: tableLabel(table), protocol },
          filename: table.universeType, universePeriod: period, protocol, entityLabel: entity,
          timeliness: result.timeliness || null, universeRowCount: result.totalRows || result.stats?.totalRows || 0,
          errorCountTotal: totalIssues, errors: result.errors || [], userEmail: userEmail(user),
        };
        const fd = new FormData();
        if (file) fd.append('file', file, file.name);
        fd.append('payload', JSON.stringify(payload));
        let d;
        if (resubmit) {
          d = await postForm(`/resubmit/${resubmit.id}`, fd, 'PUT');
          const held = d.held ? ' The corrected file is held, encrypted, until compliance reviews it.' : '';
          setDone({ text: `Submission #${resubmit.id} resubmitted with your corrected file. Compliance has been notified.${held}` });
          onDone && onDone('resubmitted');
        } else {
          d = await postForm('/submit-final', fd);
          const held = d.held ? ` The file is held, encrypted, until compliance reviews it${d.held.expires_at ? ` (automatic destruction ${new Date(d.held.expires_at).toLocaleDateString()})` : ''}.` : '';
          setDone({ text: `Submitted for compliance review as submission #${d.submissionId}.${held}` });
        }
        return;
      }
      if (!slotId) { setNote('Pick the audit universe slot first.'); setBusy(false); return; }
      const cleanErrors = (result.errors || []).map((e) => ({ row: e && e.row, field: e && e.field, category: (e && (e.category || e.type)) || 'other' }));
      const payload = {
        universe_request_id: slotId, errors: cleanErrors, timeliness: result.timeliness || null, timeliness_summary: result.timelinessSummary || null,
        selectedTable: { universeType: table.universeType, label: tableLabel(table), protocol }, universePeriod: period, row_count: result.totalRows || 0, filename: table.universeType,
      };
      const fd = new FormData();
      fd.append('file', file, file.name); fd.append('payload', JSON.stringify(payload));
      const d = await postForm('/file-to-audit', fd);
      const f = d.filed || {};
      setDone({ text: `Filed into the audit: ${f.slot || 'the universe slot'}. ${f.rows != null ? `${fmtNum(f.rows)} rows` : 'The universe file'}, ${fmtNum(f.errors)} errors recorded by type, validation report saved.` });
    } catch (e) { setNote(e.message); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title="Submit as final">
      <p className="text-sm text-slate-500 -mt-2 mb-4">{tableLabel(table)}{myCode ? ` (${myCode})` : ''} · {fmtNum(result?.totalRows || 0)} rows · {totalIssues > 0 ? `${fmtNum(totalIssues)} issue${totalIssues === 1 ? '' : 's'}` : 'no issues'}</p>
      {done ? (
        <>
          <Notice tone="emerald">{done.text}</Notice>
          <div className="flex justify-end mt-4"><button type="button" className={btn.primary} onClick={onClose}>Close</button></div>
        </>
      ) : (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Whose universe is this?</label>
            <select className={input} value={entity} onChange={(e) => setEntity(e.target.value)} disabled={busy}>
              <option value="Internal">Internal</option>
              {entities.map((e) => <option key={e.id} value={e.name}>{e.name}</option>)}
            </select>
            <div className="text-[11px] text-slate-500 mt-1">Compliance scores the COA row from the submission with this name. Nothing is typed; the names come from FDR Oversight.</div>
          </div>
          {!resubmit && (
            <div className="space-y-2">
              <OptionCard checked={dest === 'monitoring'} onChange={() => setDest('monitoring')} disabled={busy} title="Compliance review">
                <span className="block text-xs text-slate-600">The original file and the scrubbed results go to compliance, encrypted and held until the review. On their decision the file is destroyed; only the PHI free summary remains for the oversight calendar.</span>
              </OptionCard>
              <OptionCard checked={dest === 'audit'} onChange={() => setDest('audit')} disabled={busy || !slots.length}
                title={<>An open audit{orgName ? <span className="font-normal text-slate-500"> · {orgName}</span> : null}</>}>
                <span className="block text-xs text-slate-600">Files the whole universe, every error by type and the timeliness onto the audit's {myCode ? `${myCode} ` : ''}universe slot.</span>
                {!slots.length && <span className="block text-xs text-slate-400 mt-1">{audits === null ? 'Checking for open audits…' : myCode ? `No open audit is awaiting a ${myCode} universe.` : 'No open audits with universe slots.'}</span>}
                {dest === 'audit' && slots.length > 0 && (
                  <select className={`${input} mt-2`} value={slotId} onChange={(e) => setSlotId(e.target.value)} disabled={busy}>
                    <option value="">Pick the audit…</option>
                    {slots.map((o) => <option key={o.id} value={o.id}>{o.label}{o.status === 'received' ? ' (already received)' : ''}</option>)}
                  </select>
                )}
              </OptionCard>
            </div>
          )}
          {!file && dest === 'monitoring' && !resubmit && <Notice tone="amber">The original file is no longer on this screen. Run the validation again before submitting.</Notice>}
          {note && <Notice tone="red">{note}</Notice>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className={btn.secondary} onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className={btn.primary} onClick={confirm} disabled={busy || (dest === 'audit' && !slotId) || (dest === 'monitoring' && !resubmit && !file)}>{busy ? 'Submitting…' : dest === 'audit' ? 'File into the audit' : resubmit ? 'Resubmit' : 'Submit for review'}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
